import React, { memo, useCallback, useEffect, useState } from 'react'

import { AppId } from '@inno-tools/container-bridge'
import { Button, ButtonColors, ButtonVariants } from '@inno-ui/button'
import { IconScale, IconSizes, LockShieldLineIcon } from '@inno-ui/icons'
import { Modal } from '@inno-ui/modal'
import { useAvailableApps } from 'modules/app-registry/hooks/useAvailableApps'
import { Trans, useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  useGetOrganizationSettingsQuery,
  useGetUserQuery
} from 'shared/api/auth/requests'
import { FEATURE_FLAGS } from 'shared/constants/featureFlags'
import { useUserContext } from 'shared/hooks/useUserContext'
import { isProd } from 'shared/utils/isProd'

import { TwoFaModalIllustration } from './TwoFAModalIllustration'
import styles from './TwoFaReminderModal.module.scss'

export const TwoFaReminderModal = memo(() => {
  const { t } = useTranslation('translation', { keyPrefix: 'two_fa_modal' })
  const { user } = useUserContext()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const { apps } = useAvailableApps()

  const { data: userData } = useGetUserQuery(
    { id: user?.id || -1 },
    {
      enabled: Boolean(user?.id),
      staleTime: 0,
      cacheTime: 0,
      refetchOnWindowFocus: false
    }
  )

  const { data: orgSettingsData } = useGetOrganizationSettingsQuery({
    module: 'organization'
  })

  const enforce2fa =
    orgSettingsData?.data?.sections?.security?.settings?.[
      'organization.enforce_2fa'
    ]?.value === '1'

  const twofaActivationPath = `${apps[AppId.PROFILE]?.path}/security-and-privacy`

  useEffect(() => {
    if (
      userData &&
      !userData.data.twofa_enabled &&
      FEATURE_FLAGS.TWO_FA_REMINDER &&
      enforce2fa &&
      !location.pathname.includes(twofaActivationPath)
    ) {
      setIsModalOpen(true)
    }
  }, [userData, enforce2fa, location.pathname, twofaActivationPath])

  const handleOpenUrl = useCallback(
    (e: React.MouseEvent<HTMLDivElement | HTMLButtonElement>) => {
      setIsModalOpen(false)
      e.preventDefault()
      navigate(twofaActivationPath)
    },
    [navigate, twofaActivationPath]
  )

  return (
    <Modal
      closeOnClickOutside={!isProd()}
      layers={[
        {
          showHeader: false,
          body: (
            <div className={styles.body}>
              <LockShieldLineIcon
                color={'var(--warning-orange-light)'}
                scale={IconScale.Large}
                size={IconSizes.Size48}
              />
              <div className={styles.titleWrapper}>{t('title')}</div>

              <div className={styles.description}>
                <Trans
                  i18nKey='two_fa_modal.description'
                  components={{ b: <b /> }}
                />
              </div>
              <div
                className={styles.illustrationWrapper}
                onClick={handleOpenUrl}
              >
                <TwoFaModalIllustration />
              </div>
              <Button
                label={t('buttonLabel')}
                color={ButtonColors.Green}
                variant={ButtonVariants.Dark}
                onClick={handleOpenUrl}
              />
            </div>
          )
        }
      ]}
      zIndex={9}
      isOpen={isModalOpen}
      onClose={() => setIsModalOpen(false)}
      maxHeight={'min(740px, 80dvh)'}
      maxWidth={'min(760px, 75dvw)'}
      isSidebarOpen={false}
    />
  )
})
